'use client'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Loader2 } from 'lucide-react'
import type { ProviderFormState, AIProvider } from './types'
import { useTranslations } from "next-intl"



interface ProviderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  formData: ProviderFormState
  onChange: (next: ProviderFormState) => void
  onSave: () => Promise<void> | void
  isSaving: boolean
  editingProvider: AIProvider | null
}

export function ProviderDialog({
  open,
  onOpenChange,
  formData,
  onChange,
  onSave,
  isSaving,
  editingProvider,
}: ProviderDialogProps) {

  const tUi = useTranslations("Ai.AiProvidersProviderDialog")
  const tCommonUi = useTranslations("Common")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <span key={editingProvider ? tUi("editProvider") : tUi("addProvider")}>{editingProvider ? tUi("editProvider") : tUi("addProvider")}</span>
          </DialogTitle>
          <DialogDescription>
            <span key={editingProvider
              ? tUi("updateProviderConfigurationAndAPIKey")
              : tUi("addANewAIProviderWithAPICredentials")}>{editingProvider
              ? tUi("updateProviderConfigurationAndAPIKey")
              : tUi("addANewAIProviderWithAPICredentials")}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              <span><span key={tUi("providerName")}>{tUi("providerName")}</span></span> {!editingProvider && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="name"
              placeholder={"openai"}
              value={formData.name}
              onChange={(e) => onChange({ ...formData, name: e.target.value })}
              disabled={!!editingProvider}
            />
            <p className="text-xs text-gray-500">
              <span key={tUi("uniqueIdentifierLowercaseNoSpaces")}>{tUi("uniqueIdentifierLowercaseNoSpaces")}</span></p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_name">
              <span><span key={tUi("displayName")}>{tUi("displayName")}</span></span> <span className="text-red-500">*</span>
            </Label>
            <Input
              id="display_name"
              placeholder={"OpenAI"}
              value={formData.display_name}
              onChange={(e) => onChange({ ...formData, display_name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="api_key">
              <span><span key={tUi("aPIKey")}>{tUi("aPIKey")}</span></span> {!editingProvider && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="api_key"
              type="password"
              placeholder={editingProvider ? tUi("leaveEmptyToKeepCurrentKey") : "sk-..."}
              value={formData.api_key}
              onChange={(e) => onChange({ ...formData, api_key: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              <span key={tUi("aPIKeyWillBeEncryptedBeforeStorage")}>{tUi("aPIKeyWillBeEncryptedBeforeStorage")}</span></p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="base_url"><span key={tUi("baseURLOptional")}>{tUi("baseURLOptional")}</span></Label>
            <Input
              id="base_url"
              placeholder={"https://api.openai.com/v1"}
              value={formData.base_url}
              onChange={(e) => onChange({ ...formData, base_url: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              <span key={tUi("aPIEndpointURLLeaveEmptyToUseThe")}>{tUi("aPIEndpointURLLeaveEmptyToUseThe")}</span></p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_enabled"
              checked={formData.is_enabled}
              onCheckedChange={(checked) => onChange({ ...formData, is_enabled: checked })}
            />
            <Label htmlFor="is_enabled"><span key={tUi("enableProvider")}>{tUi("enableProvider")}</span></Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button onClick={onSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span key={tCommonUi("saving")}>{tCommonUi("saving")}</span></>
            ) : (
              tUi("save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
